import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { getService, services } from "@/data/services";
import { images } from "@/data/images";
import ontHoutContainer from "@/assets/ont-hout-container.jpg.asset.json";
import zakVideo from "@/assets/zakelijk-verkiezingen.mp4.asset.json";
import zakPoster from "@/assets/zakelijk-verkiezingen-poster.jpg.asset.json";
import zf1 from "@/assets/zak-foto-1.jpg.asset.json";
import zf2 from "@/assets/zak-foto-2.jpg.asset.json";
import zf3 from "@/assets/zak-foto-3.jpg.asset.json";
import zf4 from "@/assets/zak-foto-4.jpg.asset.json";
import zf5 from "@/assets/zak-foto-5.jpg.asset.json";
import zf6 from "@/assets/zak-foto-6.jpg.asset.json";
import opslagHero from "@/assets/opslag-verhuiswagens.jpeg.asset.json";

const zakFotos = [
  { src: zf1.url, alt: "Twee verhuiswagens van Vakkundig Verhuizingen & Transport klaar voor een bedrijfsverhuizing" },
  { src: zf2.url, alt: "Grote congreszaal bij World Forum Den Haag ingericht met logistieke ondersteuning van Vakkundig" },
  { src: zf6.url, alt: "Zaal van World Forum Den Haag tijdens logistieke coördinatie door Vakkundig" },
  { src: zf3.url, alt: "Logistiek van stembussen voor de verkiezingen van de Gemeente Den Haag" },
  { src: zf4.url, alt: "Getelde stembussen klaar voor transport tijdens de verkiezingen in Den Haag" },
  { src: zf5.url, alt: "Bedrijfshal met ingepakte kisten en pallets voor een zakelijke verhuizing" },
];
import { mediaForService, featuredForService } from "@/data/media";
import { Section, SectionHeading } from "@/components/site/Section";
import { MediaGallery } from "@/components/site/MediaGallery";
import { QuoteForm } from "@/components/site/QuoteForm";
import { ReviewsBlock } from "@/components/site/ReviewsBlock";
import { CtaBand } from "@/components/site/CtaBand";
import { JsonLd } from "@/components/site/JsonLd";
import { ReviewBar } from "@/components/site/ReviewBar";
import { FaqBlock } from "@/components/site/FaqBlock";
import { serviceSchema, faqSchema, breadcrumbSchema, meta } from "@/lib/schema";
import { serviceKeywords } from "@/lib/localKeywords";


export const Route = createFileRoute("/diensten/$slug")({
  loader: ({ params }) => {
    const service = getService(params.slug);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Dienst niet gevonden" }, { name: "robots", content: "noindex" }] };
    }
    const { service } = loaderData;
    return meta({
      title: service.seoTitle ?? `${service.title} | Vakkundig`,
      description: service.seoDescription ?? service.short,
      path: `/diensten/${service.slug}`,
      image: service.image,
      keywords: serviceKeywords(service),
    });
  },

  component: ServiceDetail,
});

function ServiceDetail() {
  const { service } = Route.useLoaderData();
  const others = services.filter((s) => s.slug !== service.slug);
  const allMedia = mediaForService(service.slug);
  const hero = featuredForService(service.slug);
  const galleryImages = allMedia.filter((item) => item.type === "image" && item.src !== hero?.src);
  const allVideos = allMedia.filter((item) => item.type === "video");
  const inlineImages = galleryImages.slice(0, service.body.length);
  const gallery = galleryImages.filter((item) => !inlineImages.includes(item));
  const inlineVideos = allVideos.slice(0, Math.max(service.body.length - inlineImages.length, 0));
  const inline = [...inlineImages, ...inlineVideos];
  const videos = allVideos.filter((item) => !inlineVideos.includes(item));



  return (
    <>
      <JsonLd
        data={serviceSchema({ title: service.title, description: service.short, slug: service.slug })}
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Diensten", path: "/diensten" },
          { name: service.title, path: `/diensten/${service.slug}` },
        ])}
      />
      {service.faq?.length ? <JsonLd data={faqSchema(service.faq)} /> : null}


      <section className="bg-secondary text-secondary-foreground">
        <div className="container-page grid gap-10 py-14 sm:py-18 lg:grid-cols-2 lg:items-center">
          <div>
            <nav className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">
              <Link to="/diensten">Diensten</Link> <span aria-hidden>/</span> {service.title}
            </nav>
            <h1 className="mt-4 font-display text-3xl font-extrabold sm:text-4xl">
              {service.title}
            </h1>
            <p className="mt-4 max-w-xl opacity-90">{service.intro}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/offerte"
                className="rounded-md bg-brand px-5 py-3 font-bold text-brand-foreground transition hover:bg-brand-dark"
              >
                Offerte aanvragen
              </Link>
            </div>
          </div>
          {service.slug === "zakelijke-verhuizing" ? (
            <div className="grid grid-cols-2 gap-3">
              <img
                src={images.zakelijk}
                alt="Kantoorinventaris met planten op een rolwagen tijdens een zakelijke verhuizing"
                className="aspect-[9/16] w-full rounded-2xl object-cover"
              />
              <video
                src={zakVideo.url}
                poster={zakPoster.url}
                autoPlay
                muted
                loop
                playsInline
                aria-label="Logistiek en opbouw van een stemlocatie door Vakkundig"
                className="aspect-[9/16] w-full rounded-2xl object-cover"
              />
            </div>
          ) : (
            <img
              src={service.image === "opslag" ? opslagHero.url : hero?.src ?? images[service.image]}
              alt={service.image === "opslag" ? "Verhuiswagens van Vakkundig Verhuizingen & Transport bij een bedrijfshal" : hero?.alt ?? `${service.title} door Vakkundig Verhuizingen & Transport`}
              className="max-h-[560px] w-full rounded-2xl object-cover"
            />
          )}

        </div>
      </section>

      {service.slug === "zakelijke-verhuizing" && (
        <div className="container-page grid grid-cols-2 gap-3 py-6 sm:grid-cols-3 lg:grid-cols-6">
          {zakFotos.map((f) => (
            <img key={f.src} src={f.src} alt={f.alt} loading="lazy" className="aspect-[3/4] w-full rounded-xl object-cover" />
          ))}
        </div>
      )}

      <ReviewBar />

      {service.slug === "zakelijke-verhuizing" && (
        <nav aria-label="Bedrijfsverhuizing per regio" className="container-page py-6">
          <h2 className="font-display text-xl font-bold text-secondary">Bedrijfsverhuizing per regio</h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {([
              ["den-haag", "Den Haag"],
              ["leidschendam-voorburg", "Voorburg"],
              ["zoetermeer", "Zoetermeer"],
              ["rijswijk", "Rijswijk"],
              ["delft", "Delft"],
              ["leiden", "Leiden"],
              ["rotterdam", "Rotterdam"],
            ] as const).map(([slug, city]) => (
              <li key={slug}>
                <Link
                  to="/werkgebied/$slug"
                  params={{ slug }}
                  hash="zakelijk"
                  className="inline-block rounded-full border border-border px-4 py-2 text-sm font-semibold text-secondary hover:border-brand hover:text-brand"
                >
                  Bedrijfsverhuizing {city}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/kantoorverhuizing"
                className="inline-block rounded-full border border-border px-4 py-2 text-sm font-semibold text-secondary hover:border-brand hover:text-brand"
              >
                Kantoorverhuizing: zo werkt het
              </Link>
            </li>
          </ul>
        </nav>
      )}

      <Section>
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <div>
            {service.slug === "ontruiming" && (
              <figure className="mb-8">
                <img
                  src={ontHoutContainer.url}
                  alt="Medewerker van Vakkundig laadt bewerkt hout in een container tijdens een ontruiming"
                  loading="lazy"
                  className="w-full rounded-2xl object-cover"
                />
                <figcaption className="mt-2 text-sm text-muted-foreground">
                  Gescheiden afvoer op de milieustraat: bewerkt hout netjes in de juiste container
                </figcaption>
              </figure>
            )}
            <ul className="grid gap-3 sm:grid-cols-2">
              {service.points.map((point) => (
                <li
                  key={point}
                  className="flex gap-3 rounded-lg border border-border bg-background p-4 text-sm text-secondary"
                >
                  <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
                  {point}
                </li>
              ))}
            </ul>

            <div className="mt-10 space-y-8">
              {service.body.map((block, index) => (
                <article key={block.heading}>
                  <h2 className="font-display text-2xl font-bold text-secondary">{block.heading}</h2>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{block.text}</p>
                  {inline[index] && (
                    <figure className="mt-6">
                      {inline[index].type === "video" ? (
                        <video
                          src={inline[index].src}
                          poster={inline[index].poster}
                          controls
                          preload="none"
                          playsInline
                          aria-label={inline[index].alt}
                          className="max-h-[520px] w-full rounded-2xl bg-black"
                        />
                      ) : (
                        <img
                          src={inline[index].src}
                          alt={inline[index].alt}
                          loading="lazy"
                          className="max-h-[420px] w-full rounded-2xl object-cover"
                        />
                      )}
                      {inline[index].caption && (
                        <figcaption className="mt-2 text-sm text-muted-foreground">
                          {inline[index].caption}
                        </figcaption>
                      )}
                    </figure>
                  )}
                </article>
              ))}
            </div>

          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-border bg-background p-6 shadow-[var(--shadow-card)]">
              <h2 className="font-display text-xl font-extrabold text-secondary">
                Offerte voor {service.title.toLowerCase()}
              </h2>
              <div className="mt-4">
                <QuoteForm compact service={service.title} />
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-border bg-muted p-6">
              <h2 className="font-display text-lg font-bold text-secondary">Andere diensten</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {others.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to="/diensten/$slug"
                      params={{ slug: s.slug }}
                      className="font-semibold text-secondary hover:text-brand"
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </Section>

      {gallery.length > 0 && (
        <Section>
          <SectionHeading
            eyebrow="Foto's van ons werk"
            title={`${service.title} in de praktijk`}
            intro="Onze eigen foto's en video's van deze dienst. Klik op een beeld voor een grote weergave."
          />
          <div className="mt-8">
            <MediaGallery items={gallery} />
          </div>
        </Section>
      )}

      {videos.length > 0 && (
        <Section tone="muted">
          <SectionHeading
            eyebrow="Video"
            title={`${service.title} in beeld`}
            intro="Bekijk hoe wij te werk gaan."
          />
          <div className="mx-auto mt-8 grid max-w-3xl gap-8">
            {videos.map((video) => (
              <figure key={video.src}>
                <video
                  src={video.src}
                  poster={video.poster}
                  controls
                  preload="none"
                  playsInline
                  aria-label={video.alt}
                  className="w-full rounded-2xl bg-black"
                />
                {video.caption && (
                  <figcaption className="mt-2 text-sm text-muted-foreground">{video.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        </Section>
      )}

      {service.faq?.length ? (
        <Section>
          <SectionHeading
            eyebrow="Veelgestelde vragen"
            title={`Vragen over ${service.title.toLowerCase()}`}
            intro="De vragen die wij het vaakst krijgen over deze dienst in Den Haag en de rest van Nederland."
          />
          <div className="mt-8">
            <FaqBlock items={service.faq} />
          </div>
        </Section>
      ) : null}


      <Section tone="muted">
        <SectionHeading eyebrow="Ervaringen" title="Wat klanten over ons zeggen" />
        <div className="mt-8">
          <ReviewsBlock limit={3} />
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
